import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Edit2, Trash2, X, Loader2, Save } from 'lucide-react';
import { Button } from '../../components/ui/button';
import ImageUploadField from './ImageUploadField';
import { useToast } from '../../hooks/use-toast';

/**
 * Generic CRUD manager.
 * fields: [{ name, label, type: 'text'|'textarea'|'number'|'select'|'image'|'list'|'rating', options?, placeholder?, required? }]
 */
export default function CrudManager({
  title,
  subtitle,
  fields,
  fetchFn,
  createFn,
  updateFn,
  deleteFn,
  cardRenderer,
  emptyDefaults = {},
}) {
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchFn();
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      toast({ title: 'Error', description: 'Failed to load data', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [fetchFn, toast]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    const initial = {};
    fields.forEach((f) => {
      initial[f.name] = f.type === 'list' ? [] : f.type === 'number' || f.type === 'rating' ? '' : '';
    });
    setForm({ ...initial, ...emptyDefaults });
    setEditing(null);
    setModalOpen(true);
  };

  const openEdit = (item) => {
    setForm({ ...item });
    setEditing(item);
    setModalOpen(true);
  };

  const handleField = (name, value) => setForm((p) => ({ ...p, [name]: value }));

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      // Build payload matching fields only
      const payload = {};
      fields.forEach((f) => {
        let v = form[f.name];
        if (f.type === 'number' || f.type === 'rating') v = v === '' || v == null ? 0 : Number(v);
        if (f.type === 'list') v = Array.isArray(v) ? v.filter((x) => x && x.trim()) : [];
        payload[f.name] = v === undefined ? (f.type === 'list' ? [] : '') : v;
      });
      if (editing) {
        await updateFn(editing.id, payload);
        toast({ title: 'Updated', description: `${title} updated successfully` });
      } else {
        await createFn(payload);
        toast({ title: 'Created', description: `${title} added successfully` });
      }
      setModalOpen(false);
      load();
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || 'Save failed', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;
    try {
      await deleteFn(item.id);
      toast({ title: 'Deleted', description: 'Item removed successfully' });
      load();
    } catch (err) {
      toast({ title: 'Error', description: 'Delete failed', variant: 'destructive' });
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">{title}</h1>
          {subtitle && <p className="text-slate-500 text-sm mt-1">{subtitle}</p>}
        </div>
        <Button onClick={openCreate} data-testid={`add-${title.toLowerCase().replace(/ /g, '-')}-btn`} className="bg-orange-500 hover:bg-orange-600 text-white">
          <Plus className="h-4 w-4 mr-2" /> Add New
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 text-orange-500 animate-spin" />
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-20 bg-white rounded-2xl border border-slate-200">
          <p className="text-slate-400">No items yet. Click "Add New" to create one.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((item) => (
            <div key={item.id} className="bg-white rounded-2xl border border-slate-200 overflow-hidden hover:shadow-lg transition-shadow" data-testid="admin-item-card">
              {cardRenderer(item)}
              <div className="flex gap-2 p-3 border-t border-slate-100">
                <button onClick={() => openEdit(item)} className="flex-1 flex items-center justify-center gap-1 py-2 rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100 text-sm font-medium transition-colors" data-testid="edit-item-btn">
                  <Edit2 className="h-4 w-4" /> Edit
                </button>
                <button onClick={() => handleDelete(item)} className="flex-1 flex items-center justify-center gap-1 py-2 rounded-lg bg-red-50 text-red-600 hover:bg-red-100 text-sm font-medium transition-colors" data-testid="delete-item-btn">
                  <Trash2 className="h-4 w-4" /> Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      <AnimatePresence>
        {modalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto"
            onClick={() => setModalOpen(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-2xl w-full max-w-2xl my-8 max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between p-6 border-b border-slate-200 sticky top-0 bg-white z-10">
                <h2 className="text-xl font-bold text-slate-900">{editing ? 'Edit' : 'Add'} {title.replace(/s$/, '')}</h2>
                <button onClick={() => setModalOpen(false)} className="text-slate-400 hover:text-slate-600">
                  <X className="h-6 w-6" />
                </button>
              </div>

              <form onSubmit={handleSave} className="p-6 space-y-5">
                {fields.map((f) => (
                  <FieldInput key={f.name} field={f} value={form[f.name]} onChange={(v) => handleField(f.name, v)} />
                ))}
                <div className="flex gap-3 pt-4">
                  <Button type="submit" disabled={saving} data-testid="save-item-btn" className="bg-orange-500 hover:bg-orange-600 text-white flex-1">
                    {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                    {editing ? 'Update' : 'Create'}
                  </Button>
                  <Button type="button" variant="outline" onClick={() => setModalOpen(false)}>Cancel</Button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function FieldInput({ field, value, onChange }) {
  const base = 'w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-400 focus:border-transparent outline-none';

  if (field.type === 'image') {
    return <ImageUploadField value={value} onChange={onChange} label={field.label} />;
  }

  if (field.type === 'textarea') {
    return (
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">{field.label}</label>
        <textarea rows={field.rows || 4} value={value || ''} onChange={(e) => onChange(e.target.value)} required={field.required} placeholder={field.placeholder} className={base} />
      </div>
    );
  }

  if (field.type === 'select') {
    return (
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">{field.label}</label>
        <select value={value || ''} onChange={(e) => onChange(e.target.value)} className={base}>
          {field.options.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </div>
    );
  }

  if (field.type === 'rating') {
    return (
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">{field.label}</label>
        <select value={value || 5} onChange={(e) => onChange(Number(e.target.value))} className={base}>
          {[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>{n} Star{n > 1 ? 's' : ''}</option>)}
        </select>
      </div>
    );
  }

  if (field.type === 'list') {
    const arr = Array.isArray(value) ? value : [];
    return (
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">{field.label}</label>
        <div className="space-y-2">
          {arr.map((item, i) => (
            <div key={i} className="flex gap-2">
              <input value={item} onChange={(e) => { const next = [...arr]; next[i] = e.target.value; onChange(next); }} className={base} placeholder={field.placeholder} />
              <button type="button" onClick={() => onChange(arr.filter((_, idx) => idx !== i))} className="px-3 rounded-lg bg-red-50 text-red-600 hover:bg-red-100">
                <X className="h-4 w-4" />
              </button>
            </div>
          ))}
          <button type="button" onClick={() => onChange([...arr, ''])} className="text-sm text-orange-600 font-medium hover:text-orange-700 flex items-center gap-1">
            <Plus className="h-4 w-4" /> Add item
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <label className="block text-sm font-medium text-slate-700 mb-2">{field.label}</label>
      <input type={field.type === 'number' ? 'number' : 'text'} value={value ?? ''} onChange={(e) => onChange(e.target.value)} required={field.required} placeholder={field.placeholder} className={base} />
    </div>
  );
}
